import {
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  message,
} from "antd";
import Button from "atoms/Button";
import queryKeys from "common/queryKey";
import { useUpdate } from "hooks/useUpdate";
import { CALL_API } from "common/API";
import { find, inRange, some } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { findWeeksInRange, generateSchedule, momentDate } from "utils/Date";
import useResourceFn from "./useResourceFn";
import { useDispatch, useSelector } from "react-redux";
import { getUserList } from "redux/selectors";
import { setTempEngagementFormData } from "redux/actions";

const labelStyle = {
  display: "inline-block",
  textAlign: "start",
};

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
    padding-top: 20px;
    padding-bottom: 30px;
  }
  .ant-modal-title {
    font-weight: bold;
    font-weight: 1.2rem;
  }
  .ant-modal-body {
    padding-top: 0px;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-form-item-label {
    font-weight: 500;
  }
  .ant-picker,
  .ant-input-number {
    width: 100%;
  }
`;

const EditResourceModal = ({
  show,
  onClose,
  header,
  selectedResource,
  bookedUserList,
  refetch,
  engagementData,
  tempEngagementFormData,
  setIsResource
}) => {
  const [dayHour, setDayHour] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const filterUserBookedList = bookedUserList?.filter(
    (user) =>
      user.userId === selectedResource?.userId &&
      user?.resourceId !== selectedResource?.resourceId
  );
  const dispatch = useDispatch();

  //? Temporary fix for Enabling resource booking for the same date
  const actualBookedDates = [];

  const [form] = Form.useForm();

  const { updateResource, createResource } = queryKeys;

  const { mutate: updateData, isLoading: isUpdating } = useUpdate(
    updateResource.key,
    updateResource.url
  );

  // --- POST wrapper using CALL_API ---
  const createResourceData = (payload, { onSuccess, onError } = {}) => {
    setIsCreating(true);
    dispatch(
      CALL_API({
        url: createResource.url,
        method: "POST",
        data: payload,
        onSuccess: (response) => {
          setIsCreating(false);
          onSuccess?.(response);
        },
        onError: (error) => {
          setIsCreating(false);
          onError?.(error);
        },
      })
    );
  };

  // --- chooses PATCH vs POST based on resourceId ---
  const saveResource = (payload, callbacks) => {
    if (selectedResource?.resourceId) {
      updateData(payload, callbacks); // PATCH - resource already exists
    } else {
      createResourceData(payload, callbacks); // POST - new resource
    }
  };

  const { Option } = Select;

  const justificationOptions = [
    "Availability",
    "Expense/Experience",
    "Subject Matter Expert",
    "Cost/Value",
    "Client Experience/History",
    "Technical/Education Qualification",
  ];

  const formatDate = (date) => {
    return date ? moment(date) : moment();
  };
  const userList = useSelector((state) => getUserList(state));

  useEffect(() => {
    setPersonDay(0);
    setDayHour(0);
  }, []);

  useEffect(() => {
    const selecetedPartner =
      tempEngagementFormData?.partner ?? engagementData?.stakeHolder?.partner;
    form.setFieldsValue({
      selected_resource: selectedResource?.employeeName,
      partner: find(userList, {
        userid: selecetedPartner,
      })?.fullname,
      person_day: selectedResource?.personDay,
      booked_start_date: formatDate(selectedResource?.actualBookedStartDate),
      booked_finish_date: formatDate(selectedResource?.actualBookedEndDate),
      effort: selectedResource?.effort,
      project_role: selectedResource?.projectRole,
      status: selectedResource?.status,
      remark: selectedResource?.remarks,
      justification: selectedResource?.justification,
      resourceExtRate:
        selectedResource?.personDay * selectedResource?.resourceExtRate * 8,
    });
    calculateEffort();
    workingdays();
  }, [selectedResource]);

  const plannedStartDate = tempEngagementFormData?.plan_start_date
    ? moment(tempEngagementFormData?.plan_start_date)
    : engagementData?.stakeHolder?.planStartDate
      ? moment(engagementData?.stakeHolder?.planStartDate, "DD-MM-YYYY")
      : null;

  const plannedEndDate = tempEngagementFormData?.plan_end_date
    ? moment(tempEngagementFormData?.plan_end_date)
    : engagementData?.stakeHolder?.planEndDate
      ? moment(engagementData?.stakeHolder?.planEndDate, "DD-MM-YYYY")
      : null;

  const restrictPastDate = (current, element, ranges) => {
    const customDate = moment("2022-08-01", "YYYY-MM-DD");

    if (plannedStartDate && plannedEndDate) {
      if (
        current.isBefore(plannedStartDate, "day") ||
        current.isAfter(plannedEndDate, "day")
      ) {
        return true;
      }
    }

    if (ranges) {
      for (let range of ranges) {
        if (
          current.isSameOrAfter(moment(range.startDate), "day") &&
          current.isSameOrBefore(moment(range.endDate).add(1, "day"), "day")
        ) {
          return true;
        }
      }
    }

    if (element === "booked_finish_date") {
      const start = form.getFieldValue("booked_start_date");
      return start ? current.isBefore(moment(start), "day") : false;
    }

    return current.isBefore(customDate, "day");
  };

  const restrictPastStartDate = (current, ranges) => {
    if (plannedStartDate && plannedEndDate) {
      if (
        current.isBefore(plannedStartDate, "day") ||
        current.isAfter(plannedEndDate, "day")
      ) {
        return true;
      }
    }

    if (ranges) {
      for (let range of ranges) {
        if (
          current.isSameOrAfter(moment(range.startDate), "day") &&
          current.isSameOrBefore(moment(range.endDate).add(1, "day"), "day")
        ) {
          return true;
        }
      }
    }

    return plannedStartDate ? current.isBefore(plannedStartDate, "day") : false;
  };


  const [days, setDays] = useState(0);
  const { calculateEffort, validateDateRange, workingdays } = useResourceFn(
    form,
    filterUserBookedList,
    setDays
  );

  const accessRoleOptions = [
    { value: "Is Auditor", label: "Is Auditor" },
    { value: "Member", label: "Member" },
  ];

  const statusList = [
    { value: "Assigned", label: "Assigned" },
    { value: "Un Assigned", label: "Un Assigned" },
  ];

  // NOTE: renamed the onFinish arg from `form` -> `values` so it no longer
  // shadows the outer `const [form] = Form.useForm()` instance.
  const onFinish = async (values) => {
    try {
      const startDate = values?.booked_start_date;
      const endDate = values?.booked_finish_date;
      const weeksInRange = findWeeksInRange(startDate, endDate);
      const prevWeekList = findWeeksInRange(
        selectedResource?.actualBookedStartDate || moment(),
        selectedResource?.actualBookedEndDate || moment()
      );
      const prevWeekListweekIds = prevWeekList?.map((item) => item.weekId);
      const loggedInUser = userList?.find(
        (user) =>
          user.userid === tempEngagementFormData?.createdBy ||
          user.userid === engagementData?.createdBy
      );
      const loggedInUserCountry = loggedInUser?.managingoffice?.[0] || engagementData?.client?.managingOffice;
      const hasSubclusterCountry = selectedResource?.subclusterbasecountry;
      setIsResource(true);
      const basePayload = {
        userId: selectedResource?.userId,
        employeeName: selectedResource?.employeeName,
        employeeId: selectedResource?.employeeId,
        accessRole: values?.access_role,
        projectRole: values?.project_role,
        actualBookedStartDate: moment(values?.booked_start_date).format("YYYY-MM-DD"),
        actualBookedEndDate: moment(values?.booked_finish_date).format("YYYY-MM-DD"),
        startDate: moment(values?.booked_start_date).format("YYYY-MM-DD"),
        endDate: moment(values?.booked_finish_date).format("YYYY-MM-DD"),
        engagementId: selectedResource?.engagementId || engagementData?.engagementId,
        engagementName: selectedResource?.engagementName || engagementData?.information?.jobTitle,
        clientId: engagementData?.clientId,
        effort: values?.effort,
        remarks: values?.remark,
        personDay: values?.person_day,
        resourceExtRate: selectedResource?.resourceExtRate,
        status: "Assigned",
        engagementStartDate: selectedResource?.engagementStartDate || engagementData?.stakeHolder?.planStartDate,
        engagementEndDate: selectedResource?.engagementEndDate || engagementData?.stakeHolder?.planEndDate,
        weekList: weeksInRange,
        previousWeekList: prevWeekListweekIds,
        resourceId: selectedResource?.resourceId,
        justification: values?.justification,
        serviceLine: engagementData?.client?.serviceLine ?? tempEngagementFormData?.service_line ?? null,
        competency: engagementData?.client?.competency ?? tempEngagementFormData?.client_competency ?? null,
      };

      if (engagementData) {
        if (hasSubclusterCountry) {
          const payload1 = {
            ...basePayload,
            managingOffice: loggedInUserCountry,
            subclusterbasecountry: selectedResource?.subclusterbasecountry,
          };
          await new Promise((resolve, reject) => {
            saveResource(payload1, {
              onSuccess: resolve,
              onError: (err) => {
                console.error("First (managingOffice) save failed:", err);
                reject(err);
              },
            });
          });
          const payload2 = {
            ...basePayload,
            managingOffice: selectedResource?.subclusterbasecountry,
            subclusterbasecountry: selectedResource?.subclusterbasecountry,
          };
          await new Promise((resolve, reject) => {
            saveResource(payload2, {
              onSuccess: () => {
                message.success("Successfully Updated");
                refetch();
                onClose();
                resolve();
              },
              onError: (err) => {
                console.error("Second (subclusterbasecountry) save failed:", err);
                reject(err);
              },
            });
          });
        } else {
          const payload = {
            ...basePayload,
            managingOffice: engagementData?.client?.managingOffice || "",
          };
          saveResource(payload, {
            onSuccess: () => {
              message.success("Successfully Updated");
              onClose();
              refetch();
            },
            onError: (err) => {
              console.error("Save failed:", err);
              message.error(
                err?.message ||
                "Something went wrong while saving. Please reach out to our technical team."
              );
            },
          });
        }
      } else {
        const payload = {
          ...basePayload,
          managingOffice: engagementData?.client?.managingOffice || "",
          ...(hasSubclusterCountry && { subclusterbasecountry: selectedResource?.subclusterbasecountry }),
        };

        // FIX: tempEngagementFormData or its resourceList can be undefined
        // (e.g. a brand-new draft engagement with no resources added yet).
        // Calling `.map` on `undefined` was throwing and landing in the
        // catch block below, which is what produced the generic error toast.
        const existingResourceList = tempEngagementFormData?.resourceList || [];

        function updateResourceList(resourceList, newPayload) {
          const { userId, projectRole } = newPayload;
          return resourceList.map((resource) => {
            if (
              resource.userId === userId &&
              resource.projectRole === projectRole
            ) {
              return { ...newPayload, resourceExtRate: resource?.resourceExtRate };
            }
            return resource;
          });
        }

        const updatedResourceList = updateResourceList(
          existingResourceList,
          payload
        );
        const updatedEngagementData = {
          ...(tempEngagementFormData || {}),
          resourceList: updatedResourceList,
        };
        dispatch(setTempEngagementFormData(updatedEngagementData));
        onClose();
      }
    } catch (error) {
      // Surface the real error message when we have one, so this doesn't
      // hide the actual problem behind a generic string next time.
      message.error(
        error?.message ||
        "Something went wrong. Please reach out to our technical team."
      );
      console.error("An error occurred in onFinish:", error);
    }
  };

  const schedule = generateSchedule(
    form?.getFieldValue("booked_start_date"),
    form?.getFieldValue("booked_finish_date")
  );

  const isAnyStakeHolder = () => {
    return (
      selectedResource?.userId === engagementData?.stakeHolder?.partner ||
      selectedResource?.userId ===
      engagementData?.stakeHolder?.personIncharge ||
      selectedResource?.userId ===
      engagementData?.stakeHolder?.relationshipPartner ||
      selectedResource?.userId === engagementData?.stakeHolder?.isauditor ||
      selectedResource?.userId === tempEngagementFormData?.partner ||
      selectedResource?.userId === tempEngagementFormData?.isauditor ||
      selectedResource?.userId === tempEngagementFormData?.person_incharge ||
      selectedResource?.userId === tempEngagementFormData?.relationship_partner
    );
  };

  const [personDay, setPersonDay] = useState(0);

  useEffect(() => {
    let value = parseFloat(
      (
        (parseFloat(form.getFieldValue("person_day")) * 8) /
        (schedule?.length || 1)
      ).toFixed(2)
    );
    setDayHour(value);
  }, [schedule, setPersonDay, personDay]);

  return (
    <StyledModal
      title={header}
      visible={show}
      onCancel={() => {
        onClose();
      }}
      width={800}
      footer={[]}
    >
      <AddResourceModalWrap>
        <Form layout="vertical" onFinish={onFinish} colon={false} form={form}>
          <div className="resource-card">
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>Partner</span>}
                  name="partner"
                  rules={[
                    {
                      required: false,
                      message: "Partner is required.",
                    },
                  ]}
                >
                  <Input placeholder="Enter" disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>Selected Resource</span>}
                  name="selected_resource"
                  rules={[
                    {
                      required: false,
                      message: "Resource is required.",
                    },
                  ]}
                >
                  <Input placeholder="Enter" disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>Booked Start Date</span>}
                  name="booked_start_date"
                  rules={[
                    {
                      required: true,
                      message: "Booked Start Date is required.",
                    },
                  ]}
                >
                  <DatePicker
                    placeholder="Select Date"
                    disabledDate={(current) =>
                      restrictPastStartDate(current, actualBookedDates)
                    }
                    onChange={() => {
                      validateDateRange();
                      workingdays();
                      calculateEffort();
                    }}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>Booked Finish Date</span>}
                  name="booked_finish_date"
                  dependencies={["booked_start_date"]}
                  rules={[
                    {
                      validator: (rule, value) => {
                        const bookedStartDate =
                          form.getFieldValue("booked_start_date");
                        if (bookedStartDate && value) {
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <DatePicker
                    placeholder="Select Date"
                    disabledDate={(current) =>
                      restrictPastDate(
                        current,
                        "booked_finish_date",
                        actualBookedDates
                      )
                    }
                    onChange={() => {
                      workingdays();
                      calculateEffort();
                    }}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <span
                  style={{
                    ...labelStyle,
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#5D5F68",
                    marginBottom: "20px",
                  }}
                >
                  Total Booked Mandays: {days}
                </span>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>Budget Mandays</span>}
                  name="person_day"
                  rules={[
                    {
                      required: true,
                      message: "Budget Mandays is required.",
                    },
                    {
                      validator: (_, value) => {
                        const numericValue = Number(value);

                        const isFloatingNumber = numericValue % 1 !== 0;

                        const floatEnd = numericValue.toString().split(".")[1];

                        if (
                          !Number.isInteger(numericValue) &&
                          !isFloatingNumber
                        ) {
                          return Promise.reject(
                            "Budget Mandays must be a number or half number"
                          );
                        }

                        if (numericValue <= 0 && value) {
                          return Promise.reject(
                            "Budget Mandays must be greater than 0."
                          );
                        }

                        if (numericValue > days) {
                          return Promise.reject(
                            `No of Days must be less than or equal to ${days}`
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter"
                    min={1}
                    max={days}
                    onChange={(e) => setPersonDay(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>Role</span>}
                  name="project_role"
                  rules={[
                    {
                      required: true,
                      message: "Role is required.",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select  role"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                    options={accessRoleOptions.map((option) => ({
                      label: option.label,
                      value: option.value,
                    }))}
                    fieldNames={{
                      label: "label",
                      value: "value",
                    }}
                    disabled={isAnyStakeHolder()}
                  />
                </Form.Item>
              </Col>

              <Form.Item
                label={<span style={labelStyle}>%Effort</span>}
                name="effort"
                rules={[
                  {
                    required: true,
                    message: "Effort is required.",
                  },
                ]}
                style={{ display: "none" }}
              >
                <Input placeholder="Enter" disabled />
              </Form.Item>
            </Row>

            <Col span={24} className="mt-2">
              <Form.Item
                label="Justification"
                name="justification"
                rules={[
                  { required: true, message: "Please select a justification" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select a justification"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {justificationOptions.map((justification) => (
                    <Option key={justification} value={justification}>
                      {justification}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </div>

          <div className="text-end">
            <Button type="primary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isUpdating || isCreating}>
              Proceed
            </Button>
          </div>
        </Form>
      </AddResourceModalWrap>
    </StyledModal>
  );
};

export default EditResourceModal;

const AddResourceModalWrap = styled.div`
  padding: 3%;
  .ant-picker {
    width: 100%;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-cell {
    padding: 5px 10px;
    font-size: 13px;
  }
  .action-status {
    background-color: #f7f7f8;
  }
  .ant-table-tbody > tr > td,
  .ant-table-thead > tr > th {
    border: 1px solid #dbdbdb;
  }
`;